import { useState, useEffect, useContext, useRef } from 'react'
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { PDFDocument } from 'pdf-lib';
import {
  useToast,
  useDisclosure
} from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from 'uuid';
import { createVideoFromPPt, editDynoUrl, fetchGql, generateOfficeEmbedLink, generatePptx, getSourceChunkId, getVimeoVideoId, pollJobStatus } from "../../utils/helper";
import { newDyno, dynoCreationData } from "../../graphql/helper";
import {
  DYNOTYPES,
  DYNOSTATUS,
  LOCALE,
  LOCALEDATE,
  DYNOCATEGORIES,
} from "../../graphql/queries";
import {
  CREATE_READING_COMPREHENSION_DYNO,
  CREATE_QUESTION_SETS_DYNO,
  CREATE_DYNO,
  CREATE_READING_DYNO_WITH_AI_CONTEXT,
  CREATE_QUESTION_SET_DYNO_WITH_AI_CONTEXT,
  CREATE_EMBED_IMAGE_EDDOC,
  CREATE_EMBED_VIDEO_EDDOC,
  CREATE_EMBED_PDF_EDDOC,
  CREATE_EMBED_AUDIO_EDDOC,
  CREATE_EMBED_PPT_EDDOC,
  CREATE_READING_DYNO_WITH_AI_CONTEXT_TEMPLATE,
  CREATE_READING_COMPREHENSION_DYNO_TEMPLATE
} from "../../graphql/mutations";
import { CustomToast } from '../../components/CustomToast';
import { useVideoUploader } from "../../hooks/useVideoUploader";
import { uploadWithSasToken, extractDetailsFromUrl, extractTextFromFile } from '../../utils/helper'
import { getCookie } from '../../utils/cookie';
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';
import { ADA, AVA, BRANDON, OLLIE, AVAVOICE, BRANDONVOICE, ADAVOICE, OLLIEVOICE} from "../../constants";
import { useStore } from '../../Store';
import { videoCreatorURL } from '../../config';
import { useTokenFetcher } from '../../hooks/useTokenFetcher';
import { useBreadcrumbs } from '../../contexts/BreadcrumbProvider';

export const useCreateNewAIDynoWithTemplate = ({selectedDynoType}) => {
  const { user, publisher } = useContext(PublisherAndUserContext);
  const { uploadVideo } = useVideoUploader();
  const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
  const navigate = useNavigate();
  const { addBreadcrumb } = useBreadcrumbs();
  
  const [loading, setLoading] = useState(false);
  const [dynoTypes, setDynoTypes] = useState([]);
  const [dynoStatuses, setDynoStatuses] = useState([]);
  const [locales, setLocales] = useState([]);
  const [localeDates, setLocaleDates] = useState([]);
  const [dynoCategories, setDynoCategories] = useState([]);
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [userMessage, setUserMessage] = useState(false);
  const [contentType, setContentType] = useState("STATIC");
  const [newFile, setNewFile] = useState({
    title: "",
    file: {},
  });
  const uploadedFileRef = useRef([]);
  const {
    isOpen,
    onOpen,
    onClose,
  } = useDisclosure();
  const {
    isOpen: isPromptOpen,
    onOpen: onPromptOpen,
    onClose: onPromptClose,
  } = useDisclosure();
  const {
    isOpen: isBrowseOpen,
    onOpen: onBrowseOpen,
    onClose: onBrowseClose,
  } = useDisclosure(); 
  const {
    isOpen: isCreateFromPDFOpen,
    onOpen: onCreateFromPDFOpen,
    onClose: onCreateFromPDFClose,
  } = useDisclosure();
  const {
    isOpen: isCreateFromPPTOpen,
    onOpen: onCreateFromPPTOpen,
    onClose: onCreateFromPPTClose,
  } = useDisclosure();
  const {
    isOpen: isEmbedFileOpen,
    onOpen: onEmbedFileOpen,
    onClose: onEmbedFileClose,
  } = useDisclosure();
  const {
    isOpen: isTokenExhaustOpen,
    onOpen: onTokenExhaustOpen,
    onClose: onTokenExhaustClose,
  } = useDisclosure();
  const {
    isOpen: isTokenExhaustUpgradeToProOpen,
    onOpen: onTokenExhaustUpgradeToProOpen,
    onClose: onTokenExhaustUpgradeToProClose,
  } = useDisclosure();
  const {
    isOpen: isUpgradeToProOpen,
    onOpen: onUpgradeToProOpen,
    onClose: onUpgradeToProClose,
  } = useDisclosure();

  const apolloClient = useApolloClient();
  const toast = useToast();
  const audioRef = useRef(null)
  const { refreshTokens } = useTokenFetcher();

  const { startJob, updateJobStatus, removeJob } = useStore((state) => state)

  const refetchTokens = () => refreshTokens(publisher?.id)

  const STATUS = {
    UPLOADING: "uploading",
    PROCESSING: "processing",
    COMPLETED: "completed"
  }

  const voice = {
    [AVA]: AVAVOICE,
    [BRANDON]: BRANDONVOICE,
    [ADA]: ADAVOICE,
    [OLLIE]: OLLIEVOICE
  }

  const fileTypePath = {
    "pdf": "pdf/parse",
    "video": "video/extract-text",
    "image": "image/extract-text",
    "audio": "audio/extract-text"
  }

  const getFileData = (fileData) => {
    const data = {
      "pdf": {
        pdfUrl: fileData.file.url,
        uuid: fileData.file.uuid,
        containerPath: `${publisher.id}/coursecontent`,
        publisherId: publisher?.id
      },
      "video": {
        videoId: fileData.file.id,
        uuid: fileData.file.id,
        publisherId: publisher?.id
      },
      "image": {
        imageUrl: fileData.file.url,
        uuid: fileData.file.uuid,
        publisherId: publisher?.id
      },
      "audio": {
        audioUrl: fileData.file.url,
        uuid: fileData.file.uuid,
        publisherId: publisher?.id
      }
    }
    return JSON.stringify(data[fileData.file.fileType])
  }

  const { data: dynoTypesRes } = useQuery(DYNOTYPES);
  const { data: dynoStatusRes } = useQuery(DYNOSTATUS);
  const { data: localeRes } = useQuery(LOCALE);
  const { data: localeDateRes } = useQuery(LOCALEDATE);
  const { data: dynoCategoriesRes } = useQuery(DYNOCATEGORIES);

  const [mutateFunction, { data }] = useMutation(CREATE_DYNO, {
    onCompleted: () => {
      setLoading(false);
      window.open(editDynoUrl(data.createDyno.id), "_self");
      // navigate("/myeddocs")
    },
    onError: () => {
      setLoading(false);
    },
  });
  const [creatingReadingDyno] = useMutation(CREATE_READING_DYNO_WITH_AI_CONTEXT, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateReadingDynoWithAIContext.id), "_self");
      // navigate("/myeddocs")
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [creatingAssessmentDyno] = useMutation(CREATE_QUESTION_SET_DYNO_WITH_AI_CONTEXT, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateQuestionSetsDynoWithAIContext.id), "_self");
      // navigate("/myeddocs")
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const [createReadingDynoWithTemplate] = useMutation(CREATE_READING_COMPREHENSION_DYNO, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateReadingComprehensionDynoWithAI.id), "_self");
    },
    onError: () => {
      setLoading(false);
      onCancel();
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const [createReadingDynoWithAITemplate] = useMutation(CREATE_READING_COMPREHENSION_DYNO_TEMPLATE, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateReadingComprehensionDynoWithAITemplate.id), "_self");
    },
    onError: () => {
      setLoading(false);
      onCancel();
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const [creatingReadingDynoWithAIContextTempplate] = useMutation(CREATE_READING_DYNO_WITH_AI_CONTEXT_TEMPLATE, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateReadingDynoWithAIContextTemplate.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const [createPracticeDynoWithTemplate] = useMutation(CREATE_QUESTION_SETS_DYNO, {
    onCompleted: (data) => {
      setLoading(false);
      refetchTokens();
      window.open(editDynoUrl(data.edshopifyCreateQuestionSetsDynoWithAI.id), "_self");
    },
    onError: () => {
      setLoading(false);
      onCancel();
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const [createEmbedImageEddoc] = useMutation(CREATE_EMBED_IMAGE_EDDOC, {
    onCompleted: (data) => {
      setLoading(false);
      window.open(editDynoUrl(data.edshopifyCreateEmbedImageDyno.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [createEmbedVideoEddoc] = useMutation(CREATE_EMBED_VIDEO_EDDOC, {
    onCompleted: (data) => {
      setLoading(false);
      window.open(editDynoUrl(data.edshopifyCreateEmbedVideoDyno.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [createEmbedPptEddoc] = useMutation(CREATE_EMBED_PPT_EDDOC, {
    onCompleted: (data) => {
      setLoading(false);
      window.open(editDynoUrl(data.edshopifyCreateEmbedPptDyno.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [createEmbedPDFEddoc] = useMutation(CREATE_EMBED_PDF_EDDOC, {
    onCompleted: (data) => {
      setLoading(false);
      window.open(editDynoUrl(data.edshopifyCreateEmbedPDFDyno.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [createEmbedAudioEddoc] = useMutation(CREATE_EMBED_AUDIO_EDDOC, {
    onCompleted: (data) => {
      setLoading(false);
      window.open(editDynoUrl(data.edshopifyCreateEmbedAudioDyno.id), "_self");
    },
    onError: () => {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Something went wrong. Please try again after sometime.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const onCancel = () => {
    setNewFile({
      title: "",
      file: {},
      voiceType: ""
    })
    setMediaUrl("")
    setUserMessage("");
    onClose();
    onPromptClose();
    onBrowseClose();
    onCreateFromPDFClose();
    onEmbedFileClose();
    onCreateFromPPTClose();
  }

  const selectMedia = (type, url, data) => { //Do not remove url
    setMediaUrl(data.url)
    setNewFile(
      {
        ...newFile,
        file: {
          name: data.name,
          size: data.size,
          type: type,
          uuid: data?.uuid || uuidv4(),
          fileType: type.split("s")[0],
          url: data.url,
          vimeoId: data.id ? data.id : ""
        }
      }
    )
  }

  const createDynoUsingPrompt = () => {
    setLoading(true);
    selectedDynoType.name === "Reading" ? 
    createReadingDynoWithAITemplate({
      variables: {
        data: { 
          userMessage, 
          dynoType: selectedDynoType.name,
          contentType: contentType
        },
        where: {
          id: publisher?.id
        }
      }
    }) :
    createPracticeDynoWithTemplate({
      variables: {
        data: { userMessage, dynoType: selectedDynoType.name },
        where: {
          id: publisher?.id
        }
      }
    })
  };

  const uploadVideoFile = async (data) => {
    uploadVideo({ title: data.name, videoFile: data.file }, (id, progress) => {
      setUploadProgress({ progress: progress, status: progress === 100 ? STATUS.PROCESSING : STATUS.UPLOADING })
      if (progress === 100) {
        polling(id, 30000)
      }
    })
  }

  const polling = async (videoId, interval = 10000) => {
    return new Promise((resolve, reject) => {
      function checkStatus() {
        fetch(postUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": getCookie("dyn_session"),
          },
          body: JSON.stringify({
            query: `
              query EdshopifyVideo($where: EdshopifyVideoWhereInput!) {
                  edshopifyVideo(where: $where) {
                      id
                      url
                      name
                      duration
                      thumbnail
                      description
                      tags {
                          tag
                      }
                      status
                      uploadProgress
                }
              }
            `,
            variables: {
              where: {
                id: publisher?.id,
                videoId: videoId
              }
            },
          }),
        }).then((res) => res.json())
          .then((data) => {
            if (data.data.edshopifyVideo.status === "available") {
              setUploadProgress({ progress: 100, status: STATUS.COMPLETED })
              apolloClient.cache.modify({
                fields: {
                  edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                    return {
                      ...existingedshopifyVideosRefs,
                      videos: existingedshopifyVideosRefs.videos.map((v) =>
                        readField("id", v) === data.data.edshopifyVideo.id ? { ...v, ...data.data.edshopifyVideo } : v
                      ),
                    };
                  },
                },
              });
              resolve()
            } else {
              setTimeout(() => checkStatus(), interval);
            }
          }).catch(reject)
      }
      checkStatus()
    })
  }

  const uploadResourceFile = async (file) => {
    try {
      setUploadProgress({ progress: 0, status: STATUS.UPLOADING});
      const sasUrl = await uploadWithSasToken(publisher?.id, file.fType, file.file)
      if (!sasUrl) return alert("SAS URL is missing");
      setUploadProgress({ progress: 100, status: STATUS.COMPLETED });
      const url = sasUrl.split("?")[0]
      const { id, folderName, fileName } = extractDetailsFromUrl(url)
      const newResource = {
        key: `${id}/${folderName}/${fileName}`,
        url: url,
        type: file.fType,
        size: file.size,
        name: file.name,
      };
      uploadedFileRef.current = {...newFile, file: {...file, url: url}}
      setNewFile({...newFile, file: {...file, url: url}})
      if (folderName === "images") {
        apolloClient.cache.modify({
          fields: {
            edshopifyImageResources(existingEdshopifyImageResourcesRefs = {}, { readField }) {
              const updatedResources = [
                newResource,
                ...existingEdshopifyImageResourcesRefs.resources,
              ];

              return {
                ...existingEdshopifyImageResourcesRefs,
                resources: updatedResources,
              };
            },
          },
        });
      }
      if (folderName === "audios") {
        apolloClient.cache.modify({
          fields: {
            edshopifyAudioResources(existingEdshopifyAudioResourcesRefs = {}, { readField }) {
              const updatedResources = [
                newResource,
                ...existingEdshopifyAudioResourcesRefs.resources,
              ];

              return {
                ...existingEdshopifyAudioResourcesRefs,
                resources: updatedResources,
              };
            },
          },
        });
      }
      if (folderName === "pdfs") {
        apolloClient.cache.modify({
          fields: {
            edshopifyPdfResources(existingEdshopifyPdfResourcesRefs = {}, { readField }) {
              const updatedResources = [
                newResource,
                ...existingEdshopifyPdfResourcesRefs.resources,
              ];

              return {
                ...existingEdshopifyPdfResourcesRefs,
                resources: updatedResources,
              };
            },
          },
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        position: "top-right",
        title: `Error uploading ${file.name}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const embedFileinDyno = () => {
    setLoading(true);
    const fileType = newFile.file.fileType;
    const variables = {
      where: {
        id: publisher?.id,
      },
      data: {
        dynoType: selectedDynoType.name,
        dynoTitle: newFile.title,
        url: newFile.file.url,
        fileName: newFile.file.name,
        vimeoId: newFile.file.vimeoId
      }
    };
  
    const embedHandlers = {
      image: createEmbedImageEddoc,
      pdf: createEmbedPDFEddoc,
      video: createEmbedVideoEddoc,
      audio: createEmbedAudioEddoc,
    };
  
    const embedFunction = embedHandlers[fileType];
    embedFunction({ variables });
  };

  const extractText = async (shouldExtractText) => {
    if(Object.keys(newFile.file).length === 0 ) {
      toast({
        position: "top-right",
        title: "Please select file",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return
    }
    setLoading(true);
    if(shouldExtractText) {
      const path = fileTypePath[newFile.file.fileType]
      const data = getFileData(uploadedFileRef.current.length === 0 ? newFile : uploadedFileRef.current)
      const response = await extractTextFromFile(path, data)
      if (!response.ok) {
        toast({
          position: "top-right",
          title: "Error extracting text",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
        return
      }
    }
    selectedDynoType.name === "Reading" ? creatingReadingDynoWithAIContextTempplate({
      variables: {
        data: {
          title: newFile.title,
          uuid: newFile.file.uuid,
          contentType: contentType
        },
        where: {
          id: publisher?.id
        }
      }
    }) : 
    creatingAssessmentDyno({
      variables: {
        data: {
          title: newFile.title,
          uuid: newFile.file.uuid
        },
        where: {
          id: publisher?.id
        }
      }
    })
  }

  const uploadAndCreateEddoc = async () => {
    setIsUploading(true);
    const arrayBuffer = await newFile.file.file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pageCount = pdfDoc.getPageCount();

    if (pageCount > 50) {
      toast({
        position: "top-right",
        title: "The PDF exceeds the 50 page limit.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsUploading(false);
    } else {
      await newFile.file.fileType === "video" ? uploadVideoFile(newFile.file) : uploadResourceFile(newFile.file)
    }
  }

  const createDyno = () => {
    const dyno = newDyno(
      { user, dynoTypes, dynoStatuses, locales, localeDates, dynoCategories },
      selectedDynoType,
      "isFeatured"
    );
    const data = dynoCreationData(dyno);
    setLoading(true);
    mutateFunction({
      variables: {
        data: data,
      },
    });
  };

  const generateVideoDyno = async () => {
    setLoading(true);
    const job_id = await createVideoFromPPt({
      pptx_path: newFile.file.url,
      title: newFile.title,
      voice: newFile.voiceType === "" ? voice[AVA] : newFile.voiceType,
    })
    if(job_id) {
      const jobKey = 'convert-video';
      const checkJobStatus = async (jobId) => {
        const res = await fetch(`${videoCreatorURL}/api/job-status/${jobId}`);
        const data = await res.json();
        return data;
      };
      const getQuery = (vimeo_url) => `
        mutation {
          edshopifyCreateEmbedVideoDyno(where: {
            id: "${publisher?.id}"
          }, data: {
            dynoType: "Video",
            dynoTitle: "${newFile.title}",
            url: "${vimeo_url}",
            fileName: "${newFile.file.name}",
            vimeoId: "${getVimeoVideoId(vimeo_url)}"
          }) {
            id
          }
        }
      `
      startJob(jobKey, job_id)
      pollJobStatus({
        jobId: job_id,
        jobKey,
        onStatusUpdate: (status) => {
          console.log({status})
          updateJobStatus(jobKey, status)
        },
        checkStatusFn: checkJobStatus,
        onFinalPostRequest: async (vimeo_url) => {
          const req = await fetchGql({query: getQuery(vimeo_url)})
          const res = await req.json();
          removeJob(jobKey);
          toast({
            position: "top-right",
            duration: null,
            isClosable: true,
            render: ({onClose}) => (
              <CustomToast
                title="Video EdDoc is created successfully!"
                linkText="View EdDoc"
                linkUrl={editDynoUrl(res.data.edshopifyCreateEmbedVideoDyno.id)}
                onClose={onClose}
              />
            )
          });
        }
      });
      setLoading(false)
      onCancel()
    } else {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Failed to create Eddoc. Please try again.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return
    }
  }

  const generatePptDyno = async () => {
    setLoading(true);
    const res = await getSourceChunkId({uuid: newFile.file.uuid, origin: "edshop"})
    const ppt_res = await generatePptx({
      source_chunk_id: res?.sourceChunkId,
      title: newFile.title,
	    template_url: "https://edshopstorage.blob.core.windows.net/courseconent/Galaxy presentation.pptx",
      uuid: newFile.file.uuid,
      container_name: publisher.id,
      folder_path: "ppts"
    })
    const embedUrl = generateOfficeEmbedLink(ppt_res.ppt_url)
    if(ppt_res?.status === "completed") {
      createEmbedPptEddoc({
        variables: {
          where: {
            id: publisher?.id,
          },
          data: {
            dynoType: "Reading",
            dynoTitle: newFile.title,
            embedUrl: embedUrl,
          }
        }
      })
    } else {
      setLoading(false);
      toast({
        position: "top-right",
        title: "Failed to create Eddoc. Please try again.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return
    }
  }

  const previewVoice = () => {
    audioRef.current = new Audio(newFile.voiceType ? voice[newFile.voiceType] : Object.values(voice)[0])
    audioRef.current.play();
  };

  useEffect(() => {
    if (dynoTypesRes && dynoTypesRes.dynoTypes) {
      setDynoTypes(dynoTypesRes.dynoTypes.filter(dt => (dt.name === "Reading" || dt.name === "Form" || dt.name === "Practice")).reverse());
    }
    if (dynoStatusRes && dynoStatusRes.dynoStatuses) {
      setDynoStatuses(dynoStatusRes.dynoStatuses);
    }
    if (localeRes && localeRes.locales) {
      setLocales(localeRes.locales);
    }
    if (localeDateRes && localeDateRes.localeDates) {
      setLocaleDates(localeDateRes.localeDates);
    }
    if (dynoCategoriesRes && dynoCategoriesRes.dynoCategories) {
      setDynoCategories(dynoCategoriesRes.dynoCategories);
    }
  }, [
    dynoTypesRes,
    dynoStatusRes,
    localeRes,
    localeDateRes,
    dynoCategoriesRes,
  ]);

  const buyCredits = () => {
    addBreadcrumb("Credits Info", "/credits-info", true)
    navigate("/credits-info")
  }

  const upgradeToPro = () => {
    addBreadcrumb("Pricing Plan", "/pricingplan", true)
    navigate("/pricingplan")
  }

  useEffect(() => {
    let cleanup;
    if (uploadProgress.progress === 100) {
      const maybeCleanup = extractText(true);
      if (typeof maybeCleanup === 'function') {
        cleanup = maybeCleanup;
      }
    }
    return cleanup;
  }, [uploadProgress.progress]);

  return {
    isOpen,
    onOpen,
    onCancel,
    createDynoUsingPrompt,
    loading,
    createDyno,
    selectMedia,
    mediaUrl,
    isPromptOpen,
    onPromptOpen,
    isBrowseOpen,
    onBrowseOpen,
    isCreateFromPDFOpen,
    onCreateFromPDFOpen,
    isCreateFromPPTOpen,
    onCreateFromPPTOpen,
    isEmbedFileOpen,
    onEmbedFileOpen,
    setNewFile,
    newFile,
    uploadAndCreateEddoc,
    isUploading,
    extractText,
    setUserMessage,
    userMessage,
    embedFileinDyno,
    uploadProgress,
    previewVoice,
    generateVideoDyno,
    audioRef,
    generatePptDyno,
    publisher,
    isTokenExhaustOpen,
    onTokenExhaustOpen,
    onTokenExhaustClose,
    buyCredits,
    isTokenExhaustUpgradeToProOpen,
    onTokenExhaustUpgradeToProOpen,
    onTokenExhaustUpgradeToProClose,
    upgradeToPro,
    isUpgradeToProOpen,
    onUpgradeToProOpen,
    onUpgradeToProClose,
    contentType,
    setContentType
  }
}

